import { BadgeWithIcon } from '@orn/shared-web/components/Badge';
import Dropdown from '@orn/shared-web/components/Dropdown';
import { AlertTriangleIcon, EyeOffIcon, RefreshIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { type FinancialAccount } from '../../hooks/queries/useFinancialAccounts';
import { useDeviceBreakpoints } from '../../hooks/useDeviceBreakpoints';
import { isCreditCardAccount } from '../../utils/account/accounts';
import { toCurrency } from '../../utils/formatting';
import AccountMetadata from '../AccountMetadata';
import CreditLimitProgressBar from '../CreditLimit/CreditLimitProgressBar';
import Entity from '../entity';
import PlaidFixConnectionButton from '../plaid/PlaidFixConnectionButton';
import SyncStatus from '../SyncStatus';

const SpinningRefreshIcon: React.FunctionComponent<{ className?: string }> = ({ className }) => {
  return <RefreshIcon className={cx('animate-spin', className)} />;
};

interface IAccountRowProps {
  financialAccount: FinancialAccount;
  onClick?: (accountId: string) => void;
  /** Force compact layout regardless of screen size (useful for narrow containers like sidebars) */
  compact?: boolean;
  /**
   * Omit the trailing block (balance + sync status, "Updating"/"Error" badge) and the
   * click/hover affordance. Use for non-interactive rows like the unsupported-accounts widget,
   * where balance and sync data don't apply.
   */
  hideTrailing?: boolean;
  /** Renders a trailing dots menu with a "Hide account…" action that invokes this callback. */
  onHide?: () => void;
}

/**
 * Renders a single financial account row with avatar, name, metadata, balance, and sync status.
 * When `onHide` is provided, a trailing dots menu exposes a destructive "Hide account…" action.
 */
const AccountRow = ({ financialAccount, onClick, compact = false, hideTrailing = false, onHide }: IAccountRowProps) => {
  const { isDesktop } = useDeviceBreakpoints();
  const useCompactLayout = compact || !isDesktop;

  const isCreditCard = isCreditCardAccount(financialAccount);
  const hasCreditLimit =
    isCreditCard &&
    financialAccount.currentBalance?.creditLimit &&
    parseFloat(financialAccount.currentBalance.creditLimit) > 0;

  const hasSyncedBefore = !!financialAccount.bankConnection?.lastSyncedAt;
  const hasPotentialSyncIssue = financialAccount.bankConnection?.hasPotentialSyncIssue;

  const needsReconnection = financialAccount.bankConnection?.needsReconnection ?? false;

  const handleClick = () => {
    if (hasSyncedBefore && onClick && !hideTrailing) {
      onClick(financialAccount.id);
    }
  };

  const isInteractive = hasSyncedBefore && !hideTrailing;

  return (
    <div
      className={cx(
        'flex items-center justify-between gap-5 px-5 py-4',
        isInteractive ? 'hover:bg-utility-brand-25 cursor-pointer' : 'cursor-default'
      )}
      onClick={handleClick}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        <Entity.Avatar entity={financialAccount} size="md" />
        <div className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
          <span className="text-md truncate font-medium">{financialAccount.name}</span>
          <div className="text-tertiary truncate text-sm font-normal">
            <AccountMetadata
              subtype={hideTrailing ? financialAccount.subtype : undefined}
              bankName={financialAccount.bank.name}
              mask={financialAccount.mask}
            />
          </div>
        </div>
      </div>

      {!hideTrailing && hasCreditLimit && !useCompactLayout && hasSyncedBefore && (
        <div className="flex flex-1 justify-center">
          <CreditLimitProgressBar
            creditLimit={parseFloat(financialAccount?.currentBalance?.creditLimit ?? '0')}
            balance={parseFloat(financialAccount?.currentBalance?.balance ?? '0')}
            className="w-60"
            textClassName="text-xs"
          />
        </div>
      )}

      {!hideTrailing && (
        <div className={cx('flex shrink-0 items-center justify-end', !useCompactLayout && 'w-[235px]')}>
          {hasSyncedBefore ? (
            <>
              {/* Desktop: horizontal layout with sync status then balance */}
              {!useCompactLayout && (
                <div className="flex items-center">
                  {needsReconnection ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <PlaidFixConnectionButton bankConnectionId={financialAccount.bankConnection.id} size="xs" />
                    </div>
                  ) : (
                    <SyncStatus className="px-2" lastSyncedAt={financialAccount.bankConnection?.lastSyncedAt} />
                  )}
                  <span className="text-md text-primary w-25 px-2 text-center font-medium">
                    {toCurrency(financialAccount.currentBalance?.balance ?? '0')}
                  </span>
                </div>
              )}
              {/* Compact/Mobile: stacked layout with balance on top, sync time underneath */}
              {useCompactLayout && (
                <div className="flex flex-col items-end">
                  <span className="text-md text-primary font-medium">
                    {toCurrency(financialAccount.currentBalance?.balance ?? '0')}
                  </span>
                  {needsReconnection ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <PlaidFixConnectionButton bankConnectionId={financialAccount.bankConnection.id} size="xs" />
                    </div>
                  ) : (
                    <SyncStatus lastSyncedAt={financialAccount.bankConnection?.lastSyncedAt} size="xs" />
                  )}
                </div>
              )}
            </>
          ) : hasPotentialSyncIssue ? (
            <BadgeWithIcon size="lg" color="error" iconLeading={AlertTriangleIcon}>
              Error
            </BadgeWithIcon>
          ) : (
            <BadgeWithIcon size="lg" color="warning" iconLeading={SpinningRefreshIcon}>
              Updating
            </BadgeWithIcon>
          )}
        </div>
      )}

      {onHide && (
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <Dropdown.Root>
            <Dropdown.DotsButton className="p-2" />
            <Dropdown.Popover className="w-min">
              <Dropdown.Menu>
                <Dropdown.Item icon={EyeOffIcon} destructive onAction={onHide}>
                  Hide account&hellip;
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
        </div>
      )}
    </div>
  );
};

export default AccountRow;
